import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { analytics } from '../services/analyticsService';

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  type?: 'website' | 'article' | 'podcast';
  publishedTime?: string;
  author?: string;
  duration?: string;
  keywords?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = "Nida Studios",
  description = "Nida Studios: In-Depth Politics, Trending Conversations & Premium Entertainment",
  image = "https://www.nidastudio.com/logo.webp",
  type = 'website',
  publishedTime,
  author,
  duration,
  keywords = "news, journalism, analysis, technology, politics, nida studios"
}) => {
  const location = useLocation();
  const canonicalUrl = `https://www.nidastudio.com${location.pathname === '/' ? '' : location.pathname}`;
  const fullTitle = title === "Nida Studios" ? title : `${title} | Nida Studios`;

  useEffect(() => {
    analytics.logPageView(location.pathname);
  }, [location.pathname]);

  let schemaData = null;

  if (type === 'article') {
    schemaData = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "headline": title,
      "description": description,
      "image": [image],
      "datePublished": publishedTime,
      "author": [{
        "@type": "Person",
        "name": author || "Nida Editorial"
      }],
      "publisher": {
        "@type": "Organization",
        "name": "Nida Studios",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.nidastudio.com/logo.webp"
        }
      }
    };
  } else if (type === 'podcast') {
    schemaData = {
      "@context": "https://schema.org",
      "@type": "PodcastEpisode",
      "name": title,
      "description": description,
      "image": image,
      "timeRequired": duration,
      "datePublished": publishedTime,
      "author": {
        "@type": "Person",
        "name": author || "Nida Audio"
      }
    };
  } else {
    schemaData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Nida Studios",
      "url": "https://www.nidastudio.com",
      "logo": "https://www.nidastudio.com/logo.webp",
      "sameAs": [
        "https://twitter.com/nidastudio",
        "https://instagram.com/nidastudio"
      ]
    };
  }

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content="index, follow" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'podcast' ? 'music.song' : type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Nida Studios" />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@nidastudio" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Article Specific */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Schema.org Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
    </Helmet>
  );
};